import { Download, FileText, Printer, RefreshCw } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { commandCenterApi } from "../../api/command-center.js";
import type {
  ReportCatalog,
  ReportingOverview,
  SameRepositoryScanDelta,
} from "../../api/types.js";
import { safeConsoleHref } from "../../app/routing.js";
import { InlineError } from "../../components/AsyncState.js";
import { useApiResource } from "../../hooks/useApiResource.js";
import { ReportFolio } from "./ReportFolio.js";
import {
  compareDisabledReason,
  reportLensCopy,
  reportStamp,
  type ReportLens,
} from "./reportBriefingModel.js";
import {
  REPORT_CATALOG,
  REPORT_GROUPS,
  catalogItem,
  type ReportFormatId,
} from "./reportCatalog.js";
import {
  buildDossier,
  buildDossierHtml,
  downloadTextFile,
  evidenceFromContracts,
} from "./reportDossier.js";
import "./reports-desk.css";

function generatedAtFor(generation: number): string {
  return new Date(Date.UTC(2026, 8, 2, 12, generation, 0)).toISOString();
}

type ReportsOverviewSlice = Pick<
  ReportingOverview,
  "scopeLabel" | "limitations" | "trend" | "scanDelta"
>;

function fallbackReportsOverview(): ReportsOverviewSlice {
  const scanDelta: SameRepositoryScanDelta = {
    available: false,
    currentScanId: null,
    previousScanId: null,
    new: null,
    persistent: null,
    fixed: null,
    reopened: null,
    limitation: "Select a scan to view same-repository recorded outcome history.",
  };
  return {
    scopeLabel: "Synthetic portfolio",
    limitations: ["Synthetic source — not customer findings."],
    trend: {
      available: false,
      repositoryId: null,
      points: [],
      limitation: "Select a scan to view same-repository recorded outcome history.",
    },
    scanDelta,
  };
}

export function ReportsWorkspace({
  catalog,
  catalogError,
  catalogLoading,
  canDownload,
  overview,
  lens,
  onLensChange,
}: {
  catalog: ReportCatalog;
  catalogError: Error | null;
  catalogLoading: boolean;
  canDownload: boolean;
  overview: ReportsOverviewSlice | null;
  lens: ReportLens;
  onLensChange: (lens: ReportLens) => void;
}) {
  const resolvedOverview = overview ?? fallbackReportsOverview();
  const copy = reportLensCopy(lens);
  const stamp = reportStamp(resolvedOverview.limitations, resolvedOverview.scopeLabel);
  const compareReason = compareDisabledReason(resolvedOverview.trend, resolvedOverview.scanDelta);

  return (
    <section
      className="reports-workspace"
      data-testid="reports-page"
      aria-labelledby="reports-workspace-heading"
    >
      <div className="reports-workspace__brief">
        <div>
          <span className="page-kicker">Evidence briefing</span>
          <h2 id="reports-workspace-heading">{copy.heading}</h2>
          <p>{copy.narrative}</p>
        </div>
        <div className={`report-stamp report-stamp--${stamp.tone}`}>
          <strong>{stamp.line1}</strong>
          <span>{stamp.line2}</span>
        </div>
      </div>

      <div className="report-lens-picker" role="group" aria-label="Report lens">
        {(["executive", "reviewer", "technical"] as const).map((nextLens) => (
          <button
            key={nextLens}
            type="button"
            className={nextLens === lens ? "is-on" : ""}
            aria-pressed={nextLens === lens}
            onClick={() => onLensChange(nextLens)}
          >
            {nextLens}
          </button>
        ))}
      </div>

      <div className="reports-workspace__grid">
        <article className="reports-catalog-card">
          <h3>{copy.catalogCaption}</h3>
          {catalogLoading ? <p>Loading report catalog…</p> : null}
          {catalogError ? <p role="alert">Report catalog unavailable: {catalogError.message}</p> : null}
          {!catalogLoading && catalog.items.length === 0 ? (
            <div className="empty-state empty-state--compact">
              <h4>{copy.emptyTitle}</h4>
              <p>{copy.emptyDescription}</p>
            </div>
          ) : null}
          {catalog.items.length > 0 ? (
            <ul className="reports-catalog-list">
              {catalog.items.map((item) => {
                const safeHref = safeConsoleHref(item.downloadHref);
                return (
                  <li key={item.id}>
                    <div>
                      <strong>{item.title}</strong>
                      <span>
                        {item.periodLabel} · {item.format.toUpperCase()}
                      </span>
                      <p>{item.description}</p>
                      {item.limitations.map((limitation) => (
                        <small key={limitation}>{limitation}</small>
                      ))}
                    </div>
                    {canDownload && safeHref ? (
                      <a className="button" href={safeHref}>
                        Download
                      </a>
                    ) : (
                      <span className="note">
                        {canDownload
                          ? "No same-origin download is available for this descriptor."
                          : "Download is not advertised for this session."}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </article>

        <article className="reports-compare-card" data-testid="scan-compare">
          <h3>{copy.compareCaption}</h3>
          {compareReason ? (
            <p>Historical compare is disabled. {compareReason}</p>
          ) : (
            <dl>
              <div>
                <dt>New</dt>
                <dd>{resolvedOverview.scanDelta.new ?? "—"}</dd>
              </div>
              <div>
                <dt>Persistent</dt>
                <dd>{resolvedOverview.scanDelta.persistent ?? "—"}</dd>
              </div>
              <div>
                <dt>Fixed</dt>
                <dd>{resolvedOverview.scanDelta.fixed ?? "—"}</dd>
              </div>
            </dl>
          )}
        </article>
      </div>
    </section>
  );
}

export function ReportsPage({
  selectedScanId,
  canDownload = true,
}: {
  selectedScanId?: string | undefined;
  canDownload?: boolean | undefined;
}) {
  const [formatId, setFormatId] = useState<ReportFormatId>("soc2");
  const [generation, setGeneration] = useState(0);
  const [status, setStatus] = useState(
    "Pick a report format. The A4 folio restates admitted scan evidence.",
  );
  const loadOverview = useCallback(
    (signal: AbortSignal) => commandCenterApi.getOverview(selectedScanId, signal),
    [selectedScanId],
  );
  const loadCoverage = useCallback(
    (signal: AbortSignal) => commandCenterApi.getCoverage(selectedScanId, 1, signal),
    [selectedScanId],
  );
  const loadReports = useCallback((signal: AbortSignal) => commandCenterApi.getReports(signal), []);
  const overview = useApiResource(loadOverview);
  const coverage = useApiResource(loadCoverage);
  const catalog = useApiResource(loadReports);
  const [lens, setLens] = useState<ReportLens>("executive");
  const selected = catalogItem(formatId);
  const generatedAt = overview.data?.generatedAt ?? generatedAtFor(generation);
  const evidence = useMemo(
    () =>
      evidenceFromContracts({
        overview: overview.data ?? null,
        coverage: coverage.data ?? null,
        scanId: selectedScanId ?? null,
        loading: overview.isLoading && !overview.data && !overview.error,
      }),
    [coverage.data, overview.data, overview.error, overview.isLoading, selectedScanId],
  );
  const dossier = useMemo(
    () => buildDossier(formatId, evidence, generatedAt),
    [evidence, formatId, generatedAt],
  );

  function generateReport(): void {
    const next = generation + 1;
    setGeneration(next);
    setStatus(`Generated the ${selected.name} A4 folio. ${dossier.stamp}`);
  }

  function downloadHtml(): void {
    const suffix = `${formatId}-evidence.html`;
    const name = `${selectedScanId ?? "portfolio"}-${suffix}`;
    downloadTextFile(name, "text/html", buildDossierHtml(dossier));
    setStatus(`HTML download prepared: ${name}`);
  }

  function downloadPdfReady(): void {
    const suffix = `${formatId}-a4.pdf-ready.html`;
    const name = `${selectedScanId ?? "portfolio"}-${suffix}`;
    downloadTextFile(name, "text/html", buildDossierHtml(dossier));
    setStatus("PDF-ready A4 HTML prepared. Open and use Print → Save as PDF.");
  }

  function printA4(): void {
    const reportWindow = window.open("", "_blank", "noopener,noreferrer");
    if (!reportWindow) {
      setStatus("Browser blocked the print window. Allow popups or download the PDF-ready HTML.");
      return;
    }
    reportWindow.document.write(buildDossierHtml(dossier));
    reportWindow.document.close();
    reportWindow.focus();
    reportWindow.print();
    setStatus(`Opened A4 print dialog for ${selected.name}. Use Save as PDF.`);
  }

  function downloadJson(): void {
    const name = `${selectedScanId ?? "portfolio"}-${formatId}-evidence.json`;
    downloadTextFile(name, "application/json", JSON.stringify(dossier, null, 2));
    setStatus("JSON download prepared with no certification or compliance claim.");
  }

  function downloadAll(): void {
    const pack = REPORT_CATALOG.map((item) => buildDossier(item.id, evidence, generatedAt));
    const html = pack.map((item) => buildDossierHtml(item)).join("\n");
    const stem = `${selectedScanId ?? "portfolio"}-gold-assurance-suite`;
    downloadTextFile(`${stem}.html`, "text/html", html);
    downloadTextFile(
      `${stem}.json`,
      "application/json",
      JSON.stringify(
        {
          scanId: selectedScanId ?? null,
          generatedAt,
          papers: pack.map((item) => item.format.id),
          dossiers: pack,
        },
        null,
        2,
      ),
    );
    setStatus(
      `Prepared ${pack.length} gold folios for this scope. Not a compliance determination.`,
    );
  }

  return (
    <section className="page reports-page" aria-labelledby="reports-heading">
      <div className="page-heading">
        <div>
          <span className="page-kicker">Evidence packages</span>
          <h1 id="reports-heading">Reports</h1>
          <p>
            Recreatable A4 folios from this scan. They map admitted evidence to a named framework.
            They are not a compliance determination.
          </p>
        </div>
        <button
          className="icon-button"
          type="button"
          aria-label="Refresh reports"
          title="Refresh reports"
          disabled={overview.isRefreshing || coverage.isRefreshing || catalog.isRefreshing}
          onClick={() => {
            overview.refresh();
            coverage.refresh();
            catalog.refresh();
          }}
        >
          <RefreshCw aria-hidden="true" size={17} />
        </button>
      </div>

      <section className="evidence-decision-hero" aria-label="Report honesty">
        <div>
          <span className="page-kicker">This folio</span>
          <h2>What can we put on paper without lying?</h2>
          <p>
            {evidence.loading
              ? "Loading admitted metrics for this scan. Unavailable values will not be replaced with zero."
              : evidence.unavailable
                ? "The reporting contract did not answer. Unavailable values stay unavailable."
                : "Metrics, coverage, inventory, and limitations come from the same contracts as Overview and Coverage."}
          </p>
        </div>
        <dl className="evidence-hero-metrics">
          <div data-metric="open">
            <dt>Formats</dt>
            <dd>{REPORT_CATALOG.length}</dd>
          </div>
          <div data-metric="approved">
            <dt>Assessed controls</dt>
            <dd>0</dd>
          </div>
          <div data-metric="closed">
            <dt>Certification</dt>
            <dd>None</dd>
          </div>
        </dl>
      </section>

      {overview.error ? <InlineError error={overview.error} onRetry={overview.refresh} /> : null}

      <fieldset className="reports-formats">
        <legend className="sr-only">Report format</legend>
        {REPORT_GROUPS.map((group) => (
          <div className="reports-format-group" key={group}>
            <h2 className="reports-format-group__heading">{group}</h2>
            {REPORT_CATALOG.filter((item) => item.group === group).map((item) => (
              <button
                key={item.id}
                type="button"
                className={item.id === formatId ? "is-on" : ""}
                aria-pressed={item.id === formatId}
                aria-label={item.name}
                onClick={() => {
                  setFormatId(item.id);
                  setStatus(
                    item.id === "pqc_inventory"
                      ? "Showing the PQC inventory folio; not a quantum-safe determination."
                      : `Showing the ${item.name} A4 folio; no controls have been assessed.`,
                  );
                }}
              >
                <strong>{item.name}</strong>
                <small>{item.edition}</small>
              </button>
            ))}
          </div>
        ))}
      </fieldset>

      <div className="reports-actions">
        <button className="button button--primary" type="button" onClick={generateReport}>
          <FileText size={15} aria-hidden="true" />
          Generate {selected.name}
        </button>
        <button className="button" type="button" disabled={!canDownload} onClick={downloadHtml}>
          <Download size={14} aria-hidden="true" />
          HTML
        </button>
        <button className="button" type="button" disabled={!canDownload} onClick={downloadPdfReady}>
          <Printer size={14} aria-hidden="true" />
          A4 PDF-ready
        </button>
        <button className="button" type="button" onClick={printA4}>
          <Printer size={14} aria-hidden="true" />
          Print / save PDF
        </button>
        <button className="button" type="button" disabled={!canDownload} onClick={downloadJson}>
          JSON
        </button>
        <button className="button" type="button" disabled={!canDownload} onClick={downloadAll}>
          All {REPORT_CATALOG.length}
        </button>
        <span className="note">
          Previewing {selected.name} · {selectedScanId ?? "portfolio"} · ISO 216 A4 · recreatable
          from this scan
        </span>
      </div>
      <p className="reports-status" aria-live="polite">
        {status}
      </p>

      <div className="a4-stage">
        <ReportFolio dossier={dossier} />
      </div>

      <ReportsWorkspace
        catalog={catalog.data ?? { tenantId: "unknown", items: [] }}
        catalogError={catalog.error}
        catalogLoading={catalog.isLoading && !catalog.data}
        canDownload={canDownload}
        overview={overview.data ?? null}
        lens={lens}
        onLensChange={setLens}
      />
    </section>
  );
}
